/**
 * MiniEgoGraph — the hero "you are here" graphlet (doc 147 §5.4 redesign · task #13).
 *
 * Centered focal node, OUT neighbours arrayed left, IN neighbours arrayed
 * right. Click a neighbour to swap the focal node (the parent updates
 * workspace mode → URL → re-renders). Pure SVG, scales to its container, no
 * Sigma/WebGL — so this works in the WebGL fallback too.
 *
 * Neighbours are deduplicated by entity: if A references B via two fields,
 * one bubble is shown for B with both field names on the edge label. This
 * matches ego-graph semantics (unique nodes, not unique edges).
 */
import { useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { entityById, outgoingRefs, incomingRefs } from '../state/selectors.js';
import { Icon } from '../components/Icon.js';
import { useSvgPanZoom } from '../state/useSvgPanZoom.js';

export interface MiniEgoGraphProps {
  manifest: AtlasManifest;
  entityId: string;
  onNavigateEntity: (entityId: string) => void;
}

const MAX_PER_SIDE = 5;

/** One slot in the neighbour ring — entity ID + all fields that connect to it. */
interface NeighbourEntry {
  entityId: string;
  fields: string[];
}

function fieldType(manifest: AtlasManifest, ownerEntityId: string, fieldName: string): string | undefined {
  const owner = entityById(manifest, ownerEntityId);
  return owner?.fields.find((f) => f.name === fieldName)?.type;
}

function refMetaLines(manifest: AtlasManifest, focalId: string, neighbourId: string, field: string): string[] {
  const ref = manifest.references.find(
    (r) => r.fromField === field && ((r.from === focalId && r.to === neighbourId) || (r.from === neighbourId && r.to === focalId)),
  );
  if (!ref) return [];
  const lines = [
    `${ref.kind} · ${ref.cardinality ?? 'cardinality unknown'} · ${ref.optional ? 'optional' : 'required'}`,
  ];
  if (ref.crossDomain) lines.push('crosses domains');
  if (ref.rationale) lines.push(ref.rationale);
  return lines;
}

function dedupByTarget(refs: { to: string; fromField: string }[]): NeighbourEntry[] {
  const map = new Map<string, NeighbourEntry>();
  for (const r of refs) {
    const e = map.get(r.to);
    if (e) e.fields.push(r.fromField);
    else map.set(r.to, { entityId: r.to, fields: [r.fromField] });
  }
  return [...map.values()];
}

function dedupBySource(refs: { from: string; fromField: string }[]): NeighbourEntry[] {
  const map = new Map<string, NeighbourEntry>();
  for (const r of refs) {
    const e = map.get(r.from);
    if (e) e.fields.push(r.fromField);
    else map.set(r.from, { entityId: r.from, fields: [r.fromField] });
  }
  return [...map.values()];
}

export function MiniEgoGraph({
  manifest,
  entityId,
  onNavigateEntity,
}: MiniEgoGraphProps): React.JSX.Element | null {
  const entity = entityById(manifest, entityId);
  if (!entity) return null;

  const panZoom = useSvgPanZoom();
  const [hoverEdgeId, setHoverEdgeId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; title: string; lines: string[] } | null>(null);
  const [outVisibleCount, setOutVisibleCount] = useState(MAX_PER_SIDE);
  const [inVisibleCount, setInVisibleCount] = useState(MAX_PER_SIDE);

  const outDeduped = dedupByTarget(outgoingRefs(manifest, entityId));
  const inDeduped = dedupBySource(incomingRefs(manifest, entityId));
  const outVisible = outDeduped.slice(0, outVisibleCount);
  const inVisible = inDeduped.slice(0, inVisibleCount);
  const outOverflow = Math.max(0, outDeduped.length - outVisibleCount);
  const inOverflow = Math.max(0, inDeduped.length - inVisibleCount);

  // ViewBox dims — 600 wide, 220 tall. Focal at centre. Neighbours arc out.
  const W = 600;
  const H = 220;
  const cx = W / 2;
  const cy = H / 2;
  const sideX = { out: 80, in: W - 80 };

  const placeColumn = (n: number, startY = 30, endY = H - 30): number[] => {
    if (n === 0) return [];
    if (n === 1) return [cy];
    const step = (endY - startY) / (n - 1);
    return Array.from({ length: n }, (_, i) => startY + i * step);
  };

  const outYs = placeColumn(outVisible.length);
  const inYs = placeColumn(inVisible.length);

  return (
    <div className="mini-ego-graph">
      <div className="mini-ego-canvas">
        <div className="mini-ego-zoom-controls">
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.zoomIn} aria-label="Zoom in">
            <Icon name="zoom_in" size="sm" />
          </button>
          <span className="mini-ego-zoom-level">{panZoom.scalePercent}%</span>
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.zoomOut} aria-label="Zoom out">
            <Icon name="zoom_out" size="sm" />
          </button>
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.reset} aria-label="Reset zoom">
            <Icon name="restart_alt" size="sm" />
          </button>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-label={`${entity.name} — ego graph`}
          {...panZoom.svgProps}
        >
          <g transform={panZoom.transform}>
            {/* OUT edges — one per unique neighbour; label lists all connecting fields */}
            {outVisible.map((n, i) => (
              <g
                key={`out-edge-${n.entityId}`}
                onMouseEnter={() => {
                  setHoverEdgeId(`out-${n.entityId}`);
                  setTooltip({
                    x: (cx + sideX.out) / 2,
                    y: (cy + (outYs[i] ?? cy)) / 2,
                    title: n.fields.length === 1 ? n.fields[0]! : `${n.fields.length} fields`,
                    lines: n.fields.flatMap((f) => refMetaLines(manifest, entity.id, n.entityId, f)),
                  });
                }}
                onMouseLeave={() => {
                  setHoverEdgeId(null);
                  setTooltip(null);
                }}
              >
                <line
                  data-testid={`edge-hit-${n.entityId}`}
                  x1={cx}
                  y1={cy}
                  x2={sideX.out}
                  y2={outYs[i] ?? cy}
                  stroke="transparent"
                  strokeWidth="14"
                  style={{ cursor: 'pointer' }}
                />
                <line
                  x1={cx}
                  y1={cy}
                  x2={sideX.out}
                  y2={outYs[i] ?? cy}
                  stroke={hoverEdgeId === `out-${n.entityId}` ? 'var(--brand)' : 'var(--graph-edge-focus, #A875F5)'}
                  strokeWidth={hoverEdgeId === `out-${n.entityId}` ? '2' : '1.2'}
                  style={{
                    pointerEvents: 'none',
                    opacity: hoverEdgeId === null || hoverEdgeId === `out-${n.entityId}` ? 1 : 0.3,
                  }}
                />
                <text
                  x={(cx + sideX.out) / 2}
                  y={(cy + (outYs[i] ?? cy)) / 2 - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fontFamily="var(--ff-mono)"
                  fill="var(--text2)"
                >
                  {n.fields.length === 1
                    ? abbreviate(n.fields[0]!, 10)
                    : `${abbreviate(n.fields[0]!, 8)} +${n.fields.length - 1}`}
                </text>
              </g>
            ))}
            {/* IN edges */}
            {inVisible.map((n, i) => (
              <g
                key={`in-edge-${n.entityId}`}
                onMouseEnter={() => {
                  setHoverEdgeId(`in-${n.entityId}`);
                  setTooltip({
                    x: (cx + sideX.in) / 2,
                    y: (cy + (inYs[i] ?? cy)) / 2,
                    title: n.fields.length === 1 ? n.fields[0]! : `${n.fields.length} fields`,
                    lines: n.fields.flatMap((f) => refMetaLines(manifest, n.entityId, entity.id, f)),
                  });
                }}
                onMouseLeave={() => {
                  setHoverEdgeId(null);
                  setTooltip(null);
                }}
              >
                <line
                  data-testid={`edge-hit-${n.entityId}`}
                  x1={cx}
                  y1={cy}
                  x2={sideX.in}
                  y2={inYs[i] ?? cy}
                  stroke="transparent"
                  strokeWidth="14"
                  style={{ cursor: 'pointer' }}
                />
                <line
                  x1={cx}
                  y1={cy}
                  x2={sideX.in}
                  y2={inYs[i] ?? cy}
                  stroke={hoverEdgeId === `in-${n.entityId}` ? 'var(--brand)' : 'var(--graph-edge, rgba(248,250,252,0.4))'}
                  strokeWidth={hoverEdgeId === `in-${n.entityId}` ? '2' : '1.2'}
                  style={{
                    pointerEvents: 'none',
                    opacity: hoverEdgeId === null || hoverEdgeId === `in-${n.entityId}` ? 1 : 0.3,
                  }}
                />
                <text
                  x={(cx + sideX.in) / 2}
                  y={(cy + (inYs[i] ?? cy)) / 2 - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fontFamily="var(--ff-mono)"
                  fill="var(--text3)"
                >
                  {n.fields.length === 1
                    ? abbreviate(n.fields[0]!, 10)
                    : `${abbreviate(n.fields[0]!, 8)} +${n.fields.length - 1}`}
                </text>
              </g>
            ))}

            {/* Neighbour bubbles (left = OUT). Key and aria-label are unique per entity
                because dedupByTarget guarantees one entry per unique target entity. */}
            {outVisible.map((n, i) => (
              <g
                key={`out-bubble-${n.entityId}`}
                className="mini-ego-bubble"
                tabIndex={0}
                role="button"
                aria-label={`neighbour ${n.entityId} via ${n.fields.join(', ')} · OUT`}
                onClick={() => onNavigateEntity(n.entityId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigateEntity(n.entityId);
                  }
                }}
                onMouseEnter={() =>
                  setTooltip({
                    x: sideX.out,
                    y: outYs[i] ?? cy,
                    title: n.entityId,
                    lines: n.fields.map((f) => {
                      const t = fieldType(manifest, entity.id, f); // OUT: field lives on the focal entity
                      return t ? `${f}: ${t}` : f;
                    }),
                  })
                }
                onMouseLeave={() => setTooltip(null)}
              >
                <circle cx={sideX.out} cy={outYs[i] ?? cy} r="17" />
                <text
                  x={sideX.out}
                  y={(outYs[i] ?? cy) + 3}
                  textAnchor="middle"
                  fontSize="8"
                  fontFamily="var(--ff-mono)"
                  fontWeight="700"
                >
                  {abbreviate(n.entityId, 6)}
                </text>
              </g>
            ))}
            {/* Right = IN */}
            {inVisible.map((n, i) => (
              <g
                key={`in-bubble-${n.entityId}`}
                className="mini-ego-bubble"
                tabIndex={0}
                role="button"
                aria-label={`neighbour ${n.entityId} via ${n.fields.join(', ')} · IN`}
                onClick={() => onNavigateEntity(n.entityId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigateEntity(n.entityId);
                  }
                }}
                onMouseEnter={() =>
                  setTooltip({
                    x: sideX.in,
                    y: inYs[i] ?? cy,
                    title: n.entityId,
                    lines: n.fields.map((f) => {
                      const t = fieldType(manifest, n.entityId, f); // IN: field lives on the neighbour entity
                      return t ? `${f}: ${t}` : f;
                    }),
                  })
                }
                onMouseLeave={() => setTooltip(null)}
              >
                <circle cx={sideX.in} cy={inYs[i] ?? cy} r="17" />
                <text
                  x={sideX.in}
                  y={(inYs[i] ?? cy) + 3}
                  textAnchor="middle"
                  fontSize="8"
                  fontFamily="var(--ff-mono)"
                  fontWeight="700"
                >
                  {abbreviate(n.entityId, 6)}
                </text>
              </g>
            ))}

            {/* Focal node */}
            <g aria-label={`focal entity ${entity.name}`}>
              <circle cx={cx} cy={cy} r="28" className="mini-ego-focal-glow" />
              <circle cx={cx} cy={cy} r="18" className="mini-ego-focal" />
              <text
                x={cx}
                y={cy + 4}
                textAnchor="middle"
                fontSize="11"
                fontFamily="var(--ff-mono)"
                fontWeight="800"
                fill="var(--text-on-brand, #0E0D13)"
              >
                {abbreviate(entity.name, 10)}
              </text>
            </g>

            {/* Side labels — show raw ref counts (before dedup) so the user knows
                the total relationship count, not just unique neighbours. */}
            <text x="20" y={cy + 4} fontSize="9" fontFamily="var(--ff-mono)" fill="var(--text3)">
              OUT ({outDeduped.length})
            </text>
            <text x={W - 20} y={cy + 4} textAnchor="end" fontSize="9" fontFamily="var(--ff-mono)" fill="var(--text3)">
              IN ({inDeduped.length})
            </text>
          </g>
        </svg>
        {tooltip && (
          <div
            className="mini-ego-tooltip"
            style={{ left: `${(tooltip.x / W) * 100}%`, top: `${(tooltip.y / H) * 100}%` }}
          >
            <div className="mini-ego-tooltip-title">{tooltip.title}</div>
            {tooltip.lines.map((line, i) => (
              <div key={i} className="mini-ego-tooltip-line">{line}</div>
            ))}
          </div>
        )}
      </div>

      {(outOverflow > 0 || inOverflow > 0) && (
        <div className="mini-ego-overflow">
          {outOverflow > 0 && (
            <button
              type="button"
              className="mini-ego-overflow-chip"
              onClick={() => setOutVisibleCount(outDeduped.length)}
            >
              +{outOverflow} more OUT
            </button>
          )}
          {inOverflow > 0 && (
            <button
              type="button"
              className="mini-ego-overflow-chip"
              onClick={() => setInVisibleCount(inDeduped.length)}
            >
              +{inOverflow} more IN
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function abbreviate(s: string, max = 8): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}
